import {
  identifyLogRocketUser,
  initializeLogRocket,
  trackLogRocketEvent
} from "./chunk-HQNAMWRU.mjs";

// src/providers/segment.ts
import { Analytics } from "@segment/analytics-node";
var log = (prefix, client) => (...args) => (
  // eslint-disable-next-line no-console
  console.log(`%c[${client}:${prefix}]`, "color: #5c5cff", ...args)
);
var segmentStub = {
  identify: log("identify", "segment"),
  page: log("page", "segment"),
  track: log("track", "segment"),
  group: log("group", "segment")
};
var getSegmentInstance = (user, clientMode) => {
  if (import.meta.env.DEBUG_LOGGER || typeof import.meta.env.SEGMENT_WRITE_KEY === "undefined" || user.isSpoofing) {
    return segmentStub;
  }
  if (clientMode) {
    return window?.analytics;
  }
  return new Analytics({
    writeKey: import.meta.env.SEGMENT_WRITE_KEY,
    flushAt: 1
  });
};
var createSegmentClient = (user, clientMode = false) => {
  const { userId, accountId, email } = user;
  const segmentClient = getSegmentInstance(user, clientMode);
  const baseUserTraits = {
    env: import.meta.env.APPCUES_ENV,
    accountId,
    ...email && { email }
  };
  return {
    analyticsProvider: segmentClient,
    identify: ({ traits = {}, context = {}, identifyUserId }) => {
      const identifyProps = {
        userId: identifyUserId || userId,
        traits: {
          ...baseUserTraits,
          ...traits
        },
        context
      };
      if (clientMode) {
        segmentClient?.identify(userId, identifyProps);
      } else {
        segmentClient?.identify(identifyProps);
      }
    },
    group: ({ traits = {}, context = {} } = {}) => {
      const groupProps = {
        userId,
        groupId: accountId,
        traits: {
          ...baseUserTraits,
          ...traits
        },
        context
      };
      if (clientMode) {
        segmentClient?.group(accountId, groupProps);
      } else {
        segmentClient?.group(groupProps);
      }
    },
    page: () => {
      segmentClient?.page({
        userId,
        traits: {
          ...baseUserTraits
        }
      });
    },
    track: (properties = {}) => {
      segmentClient?.track({
        userId,
        event: properties.name,
        properties: {
          ...baseUserTraits,
          ...properties
        },
        context: {
          groupId: accountId
        }
      });
    }
  };
};

// src/selectors.ts
var selectUserId = (state) => {
  return state.account.userId || "";
};
var selectAccountId = (state) => {
  return state.account.accountId || "";
};
var selectEmail = (state) => {
  return state.account.email;
};
var selectIsSpoofing = (state) => {
  return state.account.isSpoofing || false;
};
var selectName = (state) => {
  return state.account.name;
};
var selectCompany = (state) => {
  return state.account.company;
};

// src/client.ts
var createAnalyticsClient = (state, baseProperties = {}, clientMode = false, config = {}) => {
  const segmentClient = createSegmentClient(
    {
      userId: selectUserId(state),
      accountId: selectAccountId(state),
      isSpoofing: selectIsSpoofing(state),
      email: selectEmail(state)
    },
    clientMode
  );
  const userId = selectUserId(state);
  const isSpoofing = selectIsSpoofing(state);
  if (config.logrocket && !isSpoofing) {
    initializeLogRocket(config.logrocket);
    const accountId = selectAccountId(state);
    const email = selectEmail(state);
    const name = selectName(state);
    const company = selectCompany(state);
    identifyLogRocketUser(String(userId), {
      name: name || email || "Unknown",
      email: email || "unknown@appcues.com",
      accountId: accountId || "unknown",
      company: company || "Unknown"
    });
  }
  const identifyProps = {
    identifyUserId: userId,
    traits: {
      env: import.meta.env.APPCUES_ENV,
      accountId: selectAccountId(state),
      email: selectEmail(state)
    }
  };
  segmentClient.identify(identifyProps);
  return {
    page: () => {
      segmentClient.page();
    },
    track: (type, properties = {}) => {
      const resolvedBaseProperties = Object.entries(baseProperties).reduce(
        (acc, [key, value]) => {
          acc[key] = typeof value === "function" ? value() : value;
          return acc;
        },
        {}
      );
      const flattenedProperties = {
        ...resolvedBaseProperties
      };
      if (flattenedProperties.experience && typeof flattenedProperties.experience === "object" && "id" in flattenedProperties.experience && "type" in flattenedProperties.experience) {
        const { id, type: experienceType } = flattenedProperties.experience;
        flattenedProperties.experienceId = id;
        flattenedProperties.experienceType = experienceType;
        delete flattenedProperties.experience;
      }
      segmentClient.track({
        name: type,
        ...properties,
        ...flattenedProperties
      });
      trackLogRocketEvent(type, {
        ...properties,
        ...flattenedProperties
      });
    }
  };
};
export {
  createAnalyticsClient
};
//# sourceMappingURL=client.mjs.map